import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useTrackEvent } from "../../../../common/api/useTrackEvent";
import { Typography } from "../../../../common/components";
import { Modal } from "../../../../common/components/Modal";
import { TYPOGRAPHY_TYPE } from "../../../../common/components/Typography";
import { ANALYTICS_EVENTS } from "../../../../common/constants/analytics.constants";
import { isZipCodeValid } from "../../../../common/utils";
import { ADDRESS_KEYS, STEPS } from "../../constants";
import { useSetUpSpacesStore } from "../../store";
import { AddressKey } from "../../types";
import { AddressMask } from "../AddressMask";
import { DialogStepper } from "../DialogStepper";
import { LocationTypes } from "../LocationTypes";

const SetUpAddress = () => {
    const {
        toggleVisibility,
        setStep,
        locationType,
        setLocationType,
        address,
        locationTimeZone,
        setAddress,
        setLocationTimeZone,
    } = useSetUpSpacesStore();

    const { t } = useTranslation(["common", "setUpSpaces"]);

    const { trackEvent } = useTrackEvent();

    const areFieldsValid = useCallback(() => {
        if (!locationType) {
            return false;
        }

        if (!address.street.trim()) {
            return false;
        }
        if (!address.number.trim()) {
            return false;
        }
        if (address.zipCode?.trim() && !isZipCodeValid(address.zipCode)) {
            return false;
        }
        if (!address.city.trim()) {
            return false;
        }
        if (!address.country) {
            return false;
        }
        if (!locationTimeZone) {
            return false;
        }

        return true;
    }, [
        address.city,
        address.country,
        address.number,
        address.street,
        address.zipCode,
        locationTimeZone,
        locationType,
    ]);

    const handleClickNext = () => {
        if (!areFieldsValid()) {
            return;
        }

        setStep(STEPS.ADD_SPACES);
        trackEvent(ANALYTICS_EVENTS.START_LOCATION_SETUP, {
            "Type of Location": locationType,
            City: address.city,
            Country: address.country,
        });
    };

    const handleClickBack = () => setStep(STEPS.SET_LOCATION);

    const handleChangeAddress = (prop: AddressKey, value: string) => {
        if (
            prop === ADDRESS_KEYS.ZIP_CODE &&
            value.trim() &&
            !isZipCodeValid(value)
        ) {
            return;
        }

        setAddress(prop, value);
    };

    return (
        <Modal
            title={t("setUpSpaces:address.title") || ""}
            isOpen
            hasOkButton
            hasCancelButton
            cancelText={t("common:back") || ""}
            okText={t("common:next") || ""}
            onClickClose={toggleVisibility}
            onClickOk={handleClickNext}
            onClickCancel={handleClickBack}
            isOkButtonDisabled={!areFieldsValid()}
            className="max-w-full"
        >
            <div className="flex flex-col gap-4">
                <DialogStepper
                    activeStep={
                        STEPS.SET_LOCATION
                    } /* this is defined as second step of SET_LOCATION */
                />
                <Typography type={TYPOGRAPHY_TYPE.bodySmall}>
                    {t("common:mandatoryFields")}
                </Typography>

                <Typography>
                    {t("setUpSpaces:address.typeQuestion") || ""}
                </Typography>
                <LocationTypes
                    onSelect={setLocationType}
                    selectedLocationType={locationType}
                />

                <Typography>
                    {t("setUpSpaces:address.locationQuestion") || ""}
                </Typography>
                <AddressMask
                    address={address}
                    timeZone={locationTimeZone}
                    onChangeAddress={handleChangeAddress}
                    onChangeTimeZone={(value) => setLocationTimeZone(value)}
                />
            </div>
        </Modal>
    );
};

export default SetUpAddress;
